import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { ListItemIcon, Stack } from "@mui/material";
import type { ReactNode } from "react";
import { useCallback } from "react";
import type { AggregationDescriptor, MeasureDescriptor } from "../../../../../../../../../api/types/svTypes.ts";
import { isCustomMeta, isMeasureMeta } from "../../utilities/metaUtils.ts";
import FieldTreeAddButton from "../FieldTreeAddButton.tsx";
import MetaFieldItem from "../MetaFieldItem.tsx";
import { useIsAddingFields } from "../useFieldDestination.ts";

type MeasureTreeItem = MeasureDescriptor | AggregationDescriptor;

interface Props {
  field: MeasureTreeItem;
  disabled: boolean;
  addAction?: ReactNode;
  onShowOptions?: (x: number, y: number, measure: MeasureDescriptor) => void;
}

export const MeasureFieldItem = ({ field, disabled, addAction, onShowOptions }: Props) => {
  const isAdding = useIsAddingFields();
  const handleClick = useCallback(
    (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (onShowOptions && "custom" in field) {
        onShowOptions(evt.clientX, evt.clientY, field);
      }
    },
    [field, onShowOptions]
  );
  const isCustom = isCustomMeta(field);

  return (
    <MetaFieldItem
      field={field}
      actions={
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center" }}>
          {!disabled && (addAction ?? <FieldTreeAddButton field={field} />)}
          {!isAdding && (
            <ListItemIcon className="row-actions" sx={{ minWidth: "1.25rem" }} onClick={handleClick}>
              <MoreHorizRoundedIcon color="secondary" />
            </ListItemIcon>
          )}
        </Stack>
      }
    >
      {isMeasureMeta(field) && field.isLocked && (
        <ListItemIcon
          sx={(theme) => ({
            minWidth: "1rem",
            justifyContent: "center",
            color: isCustom ? theme.palette.info.main : theme.palette.secondary.light,
          })}
        >
          <LockOutlinedIcon color="primary" sx={{ fontSize: "1rem" }} />
        </ListItemIcon>
      )}
    </MetaFieldItem>
  );
};

export default MeasureFieldItem;
